class WiggensbachTrustHighlights extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-wig-trust-slide]'));
    this.track = this.querySelector('[data-wig-trust-track]');
    this.previousButton = this.querySelector('[data-wig-trust-prev]');
    this.nextButton = this.querySelector('[data-wig-trust-next]');
    this.dots = Array.from(this.querySelectorAll('[data-wig-trust-dot]'));
    this.intervalMs = Number.parseInt(this.dataset.autoplayInterval, 10) || 6000;
    this.pauseReasons = new Set();
    this.currentIndex = Math.max(
      0,
      this.slides.findIndex((slide) => slide.classList.contains('is-active'))
    );

    if (this.slides.length < 2) return;

    this.onMouseEnter = () => this.pauseAutoplay('hover');
    this.onMouseLeave = () => this.resumeAutoplay('hover');
    this.onFocusIn = () => this.pauseAutoplay('focus');
    this.onFocusOut = () => {
      window.requestAnimationFrame(() => {
        if (!this.contains(document.activeElement)) {
          this.resumeAutoplay('focus');
        }
      });
    };
    this.onVisibilityChange = () => {
      if (document.hidden) {
        this.pauseAutoplay('visibility');
      } else {
        this.resumeAutoplay('visibility');
      }
    };
    this.onBlockSelect = (event) => {
      const selectedSlide = event.target?.closest?.('[data-wig-trust-slide]');
      if (!selectedSlide || !this.contains(selectedSlide)) return;

      const selectedIndex = this.slides.indexOf(selectedSlide);
      if (selectedIndex === -1) return;

      this.pauseAutoplay('theme-editor');
      this.goTo(selectedIndex, false);
    };
    this.onBlockDeselect = () => this.resumeAutoplay('theme-editor');

    if (this.previousButton) {
      this.previousButton.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.goTo(this.currentIndex + 1));
    }

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index));
    });

    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.setSlide(this.currentIndex, false);
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();

    if (this.onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  goTo(index, restartAutoplay = true) {
    this.setSlide(index, restartAutoplay);
  }

  setSlide(index, restartAutoplay = true) {
    const totalSlides = this.slides.length;
    if (totalSlides === 0) return;

    this.currentIndex = ((index % totalSlides) + totalSlides) % totalSlides;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === this.currentIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === this.currentIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    if (restartAutoplay) {
      this.restartAutoplay();
    }
  }

  startAutoplay() {
    if (this.timer || this.pauseReasons.size > 0 || this.prefersReducedMotion()) return;

    this.timer = window.setInterval(() => {
      this.setSlide(this.currentIndex + 1, false);
    }, this.intervalMs);
  }

  stopAutoplay() {
    if (!this.timer) return;

    window.clearInterval(this.timer);
    this.timer = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  pauseAutoplay(reason) {
    this.pauseReasons.add(reason);
    this.setLiveRegion('polite');
    this.stopAutoplay();
  }

  resumeAutoplay(reason) {
    this.pauseReasons.delete(reason);
    if (this.pauseReasons.size === 0) {
      this.setLiveRegion('off');
    }
    this.startAutoplay();
  }

  setLiveRegion(value) {
    if (this.track) {
      this.track.setAttribute('aria-live', value);
    }
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('wiggensbach-trust-highlights')) {
  customElements.define('wiggensbach-trust-highlights', WiggensbachTrustHighlights);
}
